import { Solution } from './../../models/solution/solution.model';
import { UserFilter } from './../../models/user/user-filter.model';
import { Customer, CustomerFilter, PageResult } from '../../models';
import { DatasourceFilter } from '../../models/datasource/datasources-filter.model';
import { User } from '../../models/user/user.model';
import { RegionSummary } from '../../models/cell/region.model';
import _ from 'lodash';
import { UIConfig } from './config-service';
import { CCApiService } from './api-service';
import { ApiError } from '../../components/errors';

export interface OidcClientUserSsoCredentials {
  clientId: string;
  clientSecret: string;
}

export interface OidcClientUserSsoDetail {
  credentials?: OidcClientUserSsoCredentials;
  [key: string]: unknown;
}

export class CustomerApiService {
  static ApiService: CCApiService;
  static customersAPI: string;
  static apiBasePath: string;

  static init(config: UIConfig) {
    this.ApiService = new CCApiService(config.authClientId, config.authIssuerUri);
    this.apiBasePath = config.apiBasePath;
    this.customersAPI = `${this.apiBasePath}/customers`;
  }

  static async getCustomers(filter?: CustomerFilter): Promise<PageResult<Customer>> {
    const params = new URLSearchParams(filter as Record<string, string>);
    const url = params.toString() ? `${this.customersAPI}?${params}` : this.customersAPI;
    return this.ApiService.performFetch(url).then((customers: PageResult<Customer>) => customers);
  }

  static async getCustomerTags(query = '', limit = 20): Promise<string[]> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    return this.ApiService.performFetch(`${this.customersAPI}/tags?${params}`).then((tags: string[]) => tags);
  }

  static async getCustomer(id: string): Promise<Customer> {
    return this.ApiService.performFetch(`${this.customersAPI}/${id}`).then((customer: Customer) => customer);
  }

  static async createCustomer(data: Customer): Promise<Customer> {
    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify(data),
    };
    return this.ApiService.performFetch(this.customersAPI, fetchOptions).then((resp: Customer) => resp);
  }

  static async editCustomer(id: string, data: Partial<Customer>): Promise<Customer> {
    return this._updateCustomer(id, data, 'PUT');
  }

  static async patchCustomer(id: string, data: Partial<Customer>): Promise<Customer> {
    return this._updateCustomer(id, data, 'PATCH');
  }

  private static async _updateCustomer(
    id: string,
    data: Partial<Customer>,
    method: 'PUT' | 'PATCH'
  ): Promise<Customer> {
    const fetchOptions = {
      method,
      body: JSON.stringify(data),
    };
    return this.ApiService.performFetch(`${this.customersAPI}/${id}`, fetchOptions).then((resp: Customer) => resp);
  }

  static async deleteCustomer(id: string): Promise<void> {
    const fetchOptions = {
      method: 'DELETE',
    };
    return this.ApiService.performFetch(`${this.customersAPI}/${id}`, fetchOptions);
  }

  static async getUsers(filter: UserFilter): Promise<PageResult<User>> {
    const queryString = this.getQueryString(_.omit(filter, ['customerId', 'solutionId']));
    const { customerId, solutionId } = filter;
    const url = solutionId
      ? `${this.customersAPI}/${customerId}/solutions/${solutionId}/users`
      : `${this.customersAPI}/${customerId}/users`;
    return this.ApiService.performFetch(url + queryString);
  }

  static async getUsersWithAppRoles(app: Solution): Promise<User[]> {
    return this.ApiService.performFetch(
      `${this.apiBasePath}/customers/${app.customer.id}/solutions/${app.id}/users`
    ).then((result: User[]) => result);
  }

  static async getUsersWithoutAppRoles(app: Solution): Promise<User[]> {
    return this.ApiService.performFetch(
      `${this.apiBasePath}/customers/${app.customer.id}/solutions/${app.id}/users/unassigned`
    ).then((result: User[]) => result);
  }

  static async getUser(customerId: string, user: Pick<User, 'email'>): Promise<User> {
    const url = `${this.customersAPI}/${customerId}/users/${user.email}`;
    return this.ApiService.performFetch(url);
  }

  static async getDatasources(filter: DatasourceFilter = {}): Promise<any> {
    const params = CustomerApiService.getQueryString(filter);
    const { customerId } = filter;
    return this.ApiService.performFetch(`${this.customersAPI}/${customerId}/datasources${params}`);
  }

  static getQueryString = (filter: DatasourceFilter | UserFilter = {}) => {
    const params = new URLSearchParams(filter as Record<string, string>);
    if (params && params.toString()) {
      return `?${params}`;
    }
    return '';
  };

  static async addUser(customerId: string, user: User): Promise<User> {
    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify(user),
    };
    return this.ApiService.performFetch(`${this.customersAPI}/${customerId}/users`, fetchOptions).catch(
      this._mapApiErrorCodeToMessage
    ) as Promise<User>;
  }

  static async patchUser(customerId: string, solutionId: string, user: User): Promise<User> {
    const baseUrl = solutionId
      ? `${this.customersAPI}/${customerId}/solutions/${solutionId}/users`
      : `${this.customersAPI}/${customerId}/users`;
    const url = `${baseUrl}/${user.email}`;
    const fetchOptions = {
      method: 'PATCH',
      body: JSON.stringify(user),
    };
    return this.ApiService.performFetch(url, fetchOptions).catch(this._mapApiErrorCodeToMessage) as Promise<User>;
  }

  static async removeUser(customerId: string, email: string): Promise<void> {
    const fetchOptions = {
      method: 'DELETE',
    };
    return this.ApiService.performFetch(`${this.customersAPI}/${customerId}/users/${email}`, fetchOptions);
  }

  static async actOnUserStatus(customerId: string, email: string): Promise<any> {
    const fetchOptions = {
      method: 'POST',
    };
    return this.ApiService.performFetch(`${this.customersAPI}/${customerId}/users/${email}/status`, fetchOptions);
  }

  static async getClientUserSSO(customerId: string): Promise<unknown> {
    return this.ApiService.performFetch(`${this.customersAPI}/${customerId}/client-user-sso`);
  }

  static async getOidcClientUserSSO(customerId: string): Promise<OidcClientUserSsoDetail> {
    return this.ApiService.performFetch<OidcClientUserSsoDetail>(
      `${this.customersAPI}/${customerId}/oidc-client-user-sso`
    );
  }

  static async deleteSAMLSSO(customerId: string): Promise<void> {
    const fetchOptions = {
      method: 'DELETE',
    };
    return this.ApiService.performFetch(`${this.customersAPI}/${customerId}/saml-client-user-sso`, fetchOptions);
  }

  static async deleteOIDCSSO(customerId: string): Promise<void> {
    const fetchOptions = {
      method: 'DELETE',
    };
    return this.ApiService.performFetch(`${this.customersAPI}/${customerId}/oidc-client-user-sso`, fetchOptions);
  }

  static _mapApiErrorCodeToMessage(error: any): never {
    if (error instanceof ApiError && error.message === 'E0000038') {
      error.message = 'The user cannot be added. Please contact customer support.';
    }
    throw error;
  }

  // Cell assignment endpoints
  static async getAssignedCells(
    customerId: string,
    showAll = false,
    sortField?: string,
    sortDirection?: string
  ): Promise<{ cells: any[]; hasMore: boolean; total: number }> {
    const pageSize = showAll ? 1000 : 25; // First page or "reasonable all"

    let url = `${this.customersAPI}/${customerId}/cells?page=0&size=${pageSize}`;

    // Add sorting if provided
    if (sortField && sortDirection) {
      url += `&sort=${encodeURIComponent(sortField)},${encodeURIComponent(sortDirection)}`;
    }

    const pageResult: PageResult<any> = await this.ApiService.performFetch(url);

    return {
      cells: pageResult.content || [],
      hasMore: !pageResult.last && !showAll,
      total: pageResult.totalElements || 0,
    };
  }

  static async assignCustomerToCell(customerId: string, cellName: string): Promise<void> {
    const fetchOptions = {
      method: 'PATCH',
    };
    return this.ApiService.performFetch(`${this.customersAPI}/${customerId}/cells/${cellName}/assign`, fetchOptions);
  }

  static async unassignCustomerFromCell(customerId: string, cellName: string): Promise<void> {
    const fetchOptions = {
      method: 'PATCH',
    };
    return this.ApiService.performFetch(`${this.customersAPI}/${customerId}/cells/${cellName}/unassign`, fetchOptions);
  }

  static async updateCellAccessMode(customerId: string, cellAccessMode: string): Promise<Customer> {
    const fetchOptions = {
      method: 'PATCH',
      body: JSON.stringify(cellAccessMode),
    };
    return this.ApiService.performFetch(`${this.customersAPI}/${customerId}/cellAccessMode`, fetchOptions);
  }

  // Region endpoints
  static async getCustomerRegions(customerId: string, cellType?: string): Promise<RegionSummary[]> {
    let url = `${this.customersAPI}/${customerId}/regions`;
    if (cellType) {
      url += `?cellType=${cellType}`;
    }
    return this.ApiService.performFetch(url).then((regions: RegionSummary[]) => regions);
  }

  // Enhanced bidirectional assignment methods
  static async getCustomersAssignedToCell(cellName: string): Promise<Customer[]> {
    const url = `${this.apiBasePath}/cells/${cellName}/assigned-customers`;
    return this.ApiService.performFetch(url);
  }

  // Enhanced assignment with better error handling
  static async assignCustomerToCellEnhanced(
    customerId: string,
    cellName: string
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      await this.assignCustomerToCell(customerId, cellName);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Unknown error occurred during assignment',
      };
    }
  }

  static async unassignCustomerFromCellEnhanced(
    customerId: string,
    cellName: string
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      await this.unassignCustomerFromCell(customerId, cellName);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Unknown error occurred during unassignment',
      };
    }
  }
}
